export default function CookiePolicy() {
  const cookieCategories = [
    {
      title: "Essential Cookies",
      required: true,
      description: "These cookies are strictly necessary for the website to function and cannot be switched off.",
      purpose: "Enable core website functionality, maintain user sessions, and provide security features.",
      retention: "Session or 1 year maximum",
      examples: [
        {
          name: "examlock_session",
          purpose: "Maintains your login session and user authentication",
          duration: "Browser session"
        },
        {
          name: "csrf_token", 
          purpose: "Prevents cross-site request forgery attacks",
          duration: "Browser session"
        },
        {
          name: "user_preferences",
          purpose: "Stores your language and accessibility preferences",
          duration: "1 year"
        }
      ]
    },
    {
      title: "Performance Cookies",
      required: false,
      description: "These cookies help us understand how visitors use our website so we can improve performance.",
      purpose: "Collect anonymous usage statistics, monitor site performance, and identify technical issues.",
      retention: "Up to 2 years",
      examples: [
        {
          name: "analytics_id",
          purpose: "Tracks page visits and user journeys (anonymized)",
          duration: "2 years"
        },
        {
          name: "performance_metrics",
          purpose: "Monitors page load times and technical performance",
          duration: "30 days"
        },
        {
          name: "error_tracking",
          purpose: "Helps identify and fix technical issues",
          duration: "90 days"
        }
      ]
    },
    {
      title: "Functional Cookies",
      required: false,
      description: "These cookies enable enhanced functionality and personalization features.",
      purpose: "Remember your preferences, customize your experience, and provide enhanced features.",
      retention: "Up to 1 year",
      examples: [
        {
          name: "theme_preference",
          purpose: "Remembers your dark/light mode preference",
          duration: "1 year"
        },
        {
          name: "dashboard_layout",
          purpose: "Saves your custom dashboard configuration",
          duration: "6 months"
        },
        {
          name: "notification_settings",
          purpose: "Stores your notification preferences",
          duration: "1 year"
        }
      ]
    }
  ];

  const thirdPartyServices = [
    {
      service: "Stripe",
      purpose: "Payment processing and billing management",
      cookies: "Payment session, fraud prevention, billing preferences",
      privacy: "https://stripe.com/privacy",
      retention: "Varies (session to 1 year)"
    },
    {
      service: "Google Fonts",
      purpose: "Web font delivery for better typography",
      cookies: "Font loading optimization, performance metrics",
      privacy: "https://policies.google.com/privacy",
      retention: "24 hours to 1 year"
    },
    {
      service: "Firebase",
      purpose: "Application hosting and authentication",
      cookies: "Authentication tokens, session management",
      privacy: "https://firebase.google.com/support/privacy",
      retention: "Session to 1 year"
    }
  ];

  const yourRights = [
    {
      right: "Cookie Consent Control",
      description: "You can manage your cookie preferences at any time through our cookie banner or settings page.",
      action: "Use our cookie preference center to enable/disable non-essential cookies"
    },
    {
      right: "Browser Settings",
      description: "Most browsers allow you to control cookies through their settings menu.",
      action: "Check your browser's help documentation for cookie management options"
    },
    {
      right: "Opt-Out Tools",
      description: "You can use various opt-out tools to prevent tracking across websites.",
      action: "Consider tools like browser extensions that block tracking cookies"
    },
    {
      right: "Data Deletion",
      description: "You can request deletion of data collected through cookies.",
      action: "Contact our support team to request cookie data deletion"
    }
  ];

  const lastUpdated = "September 1, 2024";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">🍪 Cookie Policy</h1>
            <p className="text-xl text-orange-100 mb-6 leading-relaxed">
              This policy explains how ExamLock uses cookies and similar technologies 
              to provide and improve our educational platform services.
            </p>
            <div className="bg-white bg-opacity-20 rounded-lg px-6 py-4 inline-block">
              <div className="font-bold">Last Updated: {lastUpdated}</div>
            </div>
          </div>
        </div>
      </div>

      {/* What Are Cookies */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-blue-800 mb-6 flex items-center">
                <span className="mr-3">💡</span>
                What Are Cookies?
              </h2>
              <div className="text-blue-700 space-y-4">
                <p>
                  Cookies are small text files that are stored on your device when you visit a website. 
                  They help websites remember information about your visit, making your experience more 
                  efficient and personalized.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-bold text-blue-800 mb-2">Session Cookies</h3>
                    <p className="text-sm text-blue-600">
                      Temporary cookies that are deleted when you close your browser. 
                      Used for essential website functionality.
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-bold text-blue-800 mb-2">Persistent Cookies</h3>
                    <p className="text-sm text-blue-600">
                      Remain on your device for a specified period. 
                      Used to remember your preferences between visits.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Categories */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">🔍 Types of Cookies We Use</h2>
              <p className="text-xl text-gray-600">
                We use different categories of cookies for various purposes
              </p>
            </div>

            <div className="grid gap-8">
              {cookieCategories.map((category, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">{category.title}</h3>
                    <div className={`px-4 py-2 rounded-lg text-sm font-bold ${
                      category.required 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {category.required ? 'Always Active' : 'Optional'}
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-6">{category.description}</p>
                  
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-bold text-gray-800 mb-2">Purpose:</h4>
                      <p className="text-gray-600 text-sm">{category.purpose}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-bold text-gray-800 mb-2">Retention:</h4>
                      <p className="text-gray-600 text-sm">{category.retention}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-800 mb-4">Examples:</h4>
                    <div className="space-y-4">
                      {category.examples.map((example, exampleIndex) => (
                        <div key={exampleIndex} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-mono text-sm bg-gray-200 px-2 py-1 rounded">
                              {example.name}
                            </span>
                            <span className="text-xs text-gray-500">{example.duration}</span>
                          </div>
                          <p className="text-gray-700 text-sm">{example.purpose}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Third Party Services */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">🔗 Third-Party Services</h2>
              <p className="text-xl text-gray-600">
                We work with trusted partners who may also set cookies
              </p>
            </div>

            <div className="space-y-6">
              {thirdPartyServices.map((service, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">{service.service}</h3>
                    <a 
                      href={service.privacy}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition duration-200"
                    >
                      View Privacy Policy
                    </a>
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <h4 className="font-bold text-gray-800 mb-2">Purpose:</h4>
                      <p className="text-gray-600 text-sm">{service.purpose}</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-2">Cookies Used:</h4>
                      <p className="text-gray-600 text-sm">{service.cookies}</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-2">Retention:</h4>
                      <p className="text-gray-600 text-sm">{service.retention}</p>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-2">Control:</h4>
                      <p className="text-gray-600 text-sm">Managed via their privacy policy</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Your Rights and Choices */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">⚖️ Your Rights and Choices</h2>
              <p className="text-xl text-gray-600">
                You have control over how cookies are used on our website
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {yourRights.map((right, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{right.right}</h3>
                  <p className="text-gray-700 mb-4">{right.description}</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-bold text-green-800 mb-2">How to Exercise:</h4>
                    <p className="text-green-700 text-sm">{right.action}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Management */}
      <div className="py-16 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">🛠️ Manage Your Cookie Preferences</h2>
            <p className="text-xl text-purple-100 mb-8 leading-relaxed">
              Take control of your privacy with our easy-to-use cookie management tools
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white bg-opacity-20 rounded-lg p-6">
                <div className="text-3xl mb-3">⚙️</div>
                <div className="font-bold text-lg mb-2">Browser Settings</div>
                <p className="text-purple-100 text-sm">Configure cookie preferences in your browser</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-6">
                <div className="text-3xl mb-3">🎛️</div>
                <div className="font-bold text-lg mb-2">Preference Center</div>
                <p className="text-purple-100 text-sm">Use our cookie preference dashboard</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-6">
                <div className="text-3xl mb-3">🚫</div>
                <div className="font-bold text-lg mb-2">Opt-Out Tools</div>
                <p className="text-purple-100 text-sm">Third-party tools to block tracking</p>
              </div>
            </div>

            <div className="space-y-4">
              <button className="bg-white text-purple-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-purple-50 transition duration-200 shadow-lg mx-2">
                🍪 Open Cookie Settings
              </button>
              <a
                href="/contact"
                className="inline-block bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-purple-600 transition duration-200 mx-2"
              >
                📧 Contact Privacy Team
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Browser Instructions */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">🌐 Browser Cookie Management</h2>
              <p className="text-xl text-gray-600">
                Instructions for popular web browsers
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { browser: "Chrome", icon: "🟢", instruction: "Settings > Privacy and security > Cookies" },
                { browser: "Firefox", icon: "🔥", instruction: "Options > Privacy & Security > Cookies" },
                { browser: "Safari", icon: "🧭", instruction: "Preferences > Privacy > Cookies and website data" },
                { browser: "Edge", icon: "🔷", instruction: "Settings > Cookies and site permissions" }
              ].map((browser, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6 text-center">
                  <div className="text-3xl mb-3">{browser.icon}</div>
                  <div className="font-bold text-gray-900 mb-2">{browser.browser}</div>
                  <p className="text-gray-600 text-sm">{browser.instruction}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-center mb-2">
                <span className="text-yellow-600 mr-2">⚠️</span>
                <span className="font-bold text-yellow-800">Important Note</span>
              </div>
              <p className="text-yellow-700 text-sm">
                Disabling essential cookies may affect website functionality. Some features may not work properly 
                if you block necessary cookies required for authentication and security.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-orange-800 mb-6 flex items-center">
                <span className="mr-3">📞</span>
                Questions About Cookies?
              </h2>
              <div className="text-orange-700 space-y-4">
                <p>
                  If you have questions about our use of cookies or need help managing your preferences, 
                  please contact our privacy team:
                </p>
                <div className="bg-white rounded-lg p-4 space-y-2">
                  <div><strong>Email:</strong> privacy@examlock.ai</div>
                  <div><strong>Subject Line:</strong> Cookie Policy Inquiry</div>
                  <div><strong>Response Time:</strong> Within 48 hours</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}